import React from 'react';
import { clear } from 'idb-keyval/dist/compat.js';

// Name of the service worker's offline request queue (see SYNC_QUEUE in public/service-worker.js).
const SYNC_QUEUE_CACHE = 'sync-queue';

// Plain inline styles / hardcoded text on purpose: the stores and
// translations may be what crashed, so the fallback depends on nothing else.
const styles = {
  wrapper: { padding: '2rem', textAlign: 'center', fontFamily: 'sans-serif' },
  button: { margin: '0.5rem', padding: '0.6rem 1.2rem' },
};

const reload = () => (window.pulseRecover ? window.pulseRecover() : window.location.reload());

// Last resort for corrupt persisted state. Unsynced work lives here, hence the confirm.
// Also drops the service worker's queued offline requests: the normal recovery keeps them,
// but a reset promises to delete unsubmitted work, and they would otherwise be sent later.
// Clears the store through idb-keyval's own connection: deleting the database
// would be blocked by that open connection. Only reloads once every cleanup succeeded.
const resetLocalData = async () => {
  const message = 'This deletes all data stored on this device, including work not yet submitted. Continue?\n'
    + 'Cela supprime toutes les données de cet appareil, y compris le travail non soumis. Continuer ?';
  if (!window.confirm(message)) return;
  // Attempt every cleanup independently: one failure must not leave the other stores
  // (or queued requests the user just agreed to delete) behind.
  const results = await Promise.allSettled([
    clear(),
    window.caches ? caches.delete(SYNC_QUEUE_CACHE) : Promise.resolve(),
    Promise.resolve().then(() => localStorage.clear()),
    Promise.resolve().then(() => sessionStorage.clear()),
  ]);
  const failures = results.filter((result) => result.status === 'rejected');
  if (failures.length > 0) {
    failures.forEach((failure) => console.error('Failed to reset local data:', failure.reason));
    window.alert('Some local data could not be reset. Close other Pulse tabs and try again.\n'
      + "Certaines données n'ont pas pu être réinitialisées. Fermez les autres onglets Pulse et réessayez.");
    return;
  }
  reload();
};

export default class ErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidMount() {
    // The bundle loaded, so allow the post-deploy recovery script to run again next time.
    try {
      sessionStorage.removeItem(window.pulseRecoveryAttemptKey || 'pulse-recovery-attempted');
    } catch (error) {
      // storage unavailable; nothing to clear
    }
  }

  componentDidCatch(error, info) {
    console.error('Pulse crashed while rendering:', error, info?.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={styles.wrapper}>
        <h3>Something went wrong / Une erreur est survenue</h3>
        <p>Reloading usually fixes this after an update. / Recharger corrige généralement ce problème après une mise à jour.</p>
        <button style={styles.button} onClick={reload}>Reload / Recharger</button>
        <button style={styles.button} onClick={resetLocalData}>Reset local data / Réinitialiser les données</button>
      </div>
    );
  }
}
